import { PlannerTaskInfo, TimelineHourRow, TimelineRow, TimelineScale } from './types';
import { getTimelineTaskRange, isAllDayTask } from './scheduleUtils';

export type HourTaskLayout = { column: number; columnCount: number };
export const timeSlots = Array.from({ length: 24 }, (_, hour) => hour);

export const getTaskDaySegment = (task: PlannerTaskInfo['task'], periodDate: Date) => {
    const { start: taskStart, end: taskEnd } = getTimelineTaskRange(task);
    const dayStart = new Date(periodDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return {
        start: new Date(Math.max(taskStart.getTime(), dayStart.getTime())),
        end: new Date(Math.min(taskEnd.getTime(), dayEnd.getTime())),
    };
};

export const getTimelineTaskRenderSegment = (
    task: PlannerTaskInfo['task'],
    periodDate: Date,
    isFirstLoadedDay: boolean,
) => {
    const { start: taskStart, end: taskEnd } = getTimelineTaskRange(task);
    if (Number.isNaN(taskStart.getTime())) return null;

    const dayStart = new Date(periodDate);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    const startsThisDay = taskStart >= dayStart && taskStart < dayEnd;

    if (startsThisDay) return { start: taskStart, end: taskEnd };
    if (isFirstLoadedDay && taskStart < dayStart && taskEnd > dayStart) {
        return { start: dayStart, end: taskEnd };
    }

    return null;
};

export const getTimelineHourRows = (): TimelineHourRow[] => timeSlots.map((hour) => ({
    startHour: hour,
    endHour: hour + 1,
    isGap: false,
}));

export const getTimelineHourOffset = (hour: number, minutes: number, rows: TimelineHourRow[], timeSlotHeight: number) => {
    const firstHour = rows[0]?.startHour || 0;
    return Math.max(0, hour - firstHour + minutes / 60) * timeSlotHeight;
};

export const getHourTaskLayouts = (taskInfos: PlannerTaskInfo[], periodDate: Date) => {
    const layouts = new Map<string, HourTaskLayout>();
    const sorted = taskInfos.filter(({ task }) => !isAllDayTask(task)).map((taskInfo) => {
        const { start, end } = getTaskDaySegment(taskInfo.task, periodDate);
        return { taskInfo, start: start.getTime(), end: end.getTime() };
    }).sort((a, b) => a.start - b.start || b.end - a.end);
    let cluster: typeof sorted = [];
    let clusterEnd = Number.NEGATIVE_INFINITY;
    const commit = () => {
        const ends: number[] = [];
        const assignments = cluster.map((entry) => {
            let column = ends.findIndex((end) => end <= entry.start);
            if (column === -1) column = ends.length;
            ends[column] = entry.end;
            return { id: entry.taskInfo.task.id, column };
        });
        assignments.forEach(({ id, column }) => layouts.set(id, { column, columnCount: Math.max(1, ends.length) }));
    };
    sorted.forEach((entry) => {
        if (cluster.length && entry.start >= clusterEnd) {
            commit();
            cluster = [];
            clusterEnd = Number.NEGATIVE_INFINITY;
        }
        cluster.push(entry);
        clusterEnd = Math.max(clusterEnd, entry.end);
    });
    commit();
    return layouts;
};

export const getPeriodDate = (selectedDate: Date, scale: TimelineScale, offset: number) => {
    const date = new Date(selectedDate);
    if (scale === 'day') {
        date.setDate(date.getDate() + offset);
        date.setHours(0, 0, 0, 0);
        return date;
    }
    if (scale === 'month') return new Date(date.getFullYear(), date.getMonth() + offset, 1);
    if (scale === 'year') return new Date(date.getFullYear() + offset, 0, 1);
    return new Date(date.getFullYear() + offset * 12, 0, 1);
};

export const getPeriodLabel = (date: Date, scale: TimelineScale, language: string) => {
    const capitalize = (label: string) => label ? label[0].toLocaleUpperCase(language) + label.slice(1) : label;
    if (scale === 'day') return capitalize(date.toLocaleDateString(language, { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }));
    if (scale === 'month') return capitalize(date.toLocaleDateString(language, { month: 'long', year: 'numeric' }));
    if (scale === 'year') return date.getFullYear().toString();
    const start = date.getFullYear() - 5;
    return `${start} - ${start + 11}`;
};

export const getEndTimeForRange = (startTime: string, duration: number) => {
    const [hour, minute] = startTime.split(':').map(Number);
    const total = minute + duration;
    return `${((hour + Math.floor(total / 60)) % 24).toString().padStart(2, '0')}:${(total % 60).toString().padStart(2, '0')}`;
};

export type TimelineRowFactory = (date: Date) => TimelineRow[];
